import React, { FC, useEffect, useState } from 'react'
import { I18n } from 'aws-amplify'
import { GameFlowSteps } from 'types'
import { Grid, makeStyles, Theme, Typography } from '@material-ui/core'
import { GameFlowStepsConfig } from '../../helpers/steps'
import classnames from 'classnames'
import { Paginator } from 'components'

import { Demos, Sessions } from 'helpers'
import { useAppState } from '../../providers'

interface ExploreProps {
  previousSceneName: GameFlowSteps
  currentSceneName: string
}

export const ExploreScene: FC<ExploreProps> = ({ currentSceneName, previousSceneName }) => {
  const classes = useStyles()
  const {
    appState: { demoImagesLoaded, sessionImagesLoaded },
    setDemoImagesLoaded,
    setSessionImagesLoaded
  } = useAppState()
  const [loading, setLoading] = useState<boolean>(true)
  const [dodCurrentPage, setDodCurrentPage] = useState<number>(window.state.getCarouselIndex('sessions') || 0)
  const [fiveGeeCurrentPage, setFiveGeeCurrentPage] = useState<number>(window.state.getCarouselIndex('demos') || 0)
  const [hideText, setHideText] = useState<boolean>(getInitialHideText()) //TODO: SET INITIAL BASED ON WINDOW VALUE

  function getInitialHideText() {
    const demoInd = window.state.getCarouselIndex('demos')
    const sessionInd = window.state.getCarouselIndex('sessions')

    if (currentSceneName === GameFlowSteps.Explore || currentSceneName === GameFlowSteps.BackToExplore) {
      return demoInd > 0
    } else {
      return sessionInd > 0
    }
  }

  useEffect(() => {
    setTimeout(
      () => {
        setLoading(false)
      },
      [GameFlowSteps.Demo, GameFlowSteps.Session].includes(previousSceneName)
        ? GameFlowStepsConfig[GameFlowSteps.BackToExplore].animation.time
        : GameFlowStepsConfig[currentSceneName].animation.time
    )
    // eslint-disable-next-line
  }, [])

  const conditionallyHideText = page => {
    if (page > 0 && !hideText) {
      setHideText(true)
    } else if (page === 0 && hideText) {
      setHideText(false)
    }
  }

  useEffect(() => {
    if (currentSceneName === GameFlowSteps.Sessions || currentSceneName === GameFlowSteps.BackToSessions) {
      conditionallyHideText(dodCurrentPage)
    } else if (currentSceneName === GameFlowSteps.Explore || currentSceneName === GameFlowSteps.BackToExplore) {
      conditionallyHideText(fiveGeeCurrentPage)
    }
    // eslint-disable-next-line
  }, [dodCurrentPage, fiveGeeCurrentPage])

  const loadImages = async () => {
    const isSessions = currentSceneName === GameFlowSteps.Sessions || currentSceneName === GameFlowSteps.BackToSessions
    if (isSessions && !sessionImagesLoaded) {
      setSessionImagesLoaded(true)
    } else if (!isSessions && !demoImagesLoaded) {
      setDemoImagesLoaded(true)
    } else {
      return
    }

    const images: string[] = (isSessions ? Sessions : Demos)
      .map(demo => {
        return [demo.poster as string, demo.end?.nextDemoThumbnail as string]
      })
      .flat()
    const imgPromises = images.map(
      imageUrl =>
        new Promise((resolve, reject) => {
          const img = new Image()
          img.src = imageUrl
          img.onload = resolve
          img.onerror = reject
        })
    )

    Promise.all(imgPromises)
  }

  useEffect(() => {
    loadImages()
    // eslint-disable-next-line
  }, [])

  return (
    <>
      {!loading && !hideText && (
        <div id='scene-explore' className={classnames(classes.transition, classes.root)}>
          <Grid container direction='column' justify='center' spacing={2}>
            <Grid item xs={12}>
              <Typography
                variant='h3'
                component='h1'
                paragraph
                dangerouslySetInnerHTML={{
                  __html:
                    currentSceneName === GameFlowSteps.Explore || currentSceneName === GameFlowSteps.BackToExplore
                      ? I18n.get('exploreSceneTitle')
                      : I18n.get('sessionsSceneTitle')
                }}
              />
            </Grid>
            <Grid item xs={12}>
              <Typography
                dangerouslySetInnerHTML={{
                  __html:
                    currentSceneName === GameFlowSteps.Explore || currentSceneName === GameFlowSteps.BackToExplore
                      ? I18n.get('exploreSceneBlurb')
                      : I18n.get('sessionsSceneBlurb')
                }}
              />
            </Grid>
          </Grid>
        </div>
      )}
      {!loading && (
        <div className={classes.navBlock}>
          {(currentSceneName === GameFlowSteps.Sessions || currentSceneName === GameFlowSteps.BackToSessions) && (
            <Paginator
              totalPages={Sessions.length}
              currentPage={dodCurrentPage}
              onPageChange={page => {
                setDodCurrentPage(page)
                window.state.scrollTo('sessions', page)
              }}
            />
          )}
          {(currentSceneName === GameFlowSteps.Explore || currentSceneName === GameFlowSteps.BackToExplore) && (
            <Paginator
              totalPages={Demos.length}
              currentPage={fiveGeeCurrentPage}
              onPageChange={page => {
                setFiveGeeCurrentPage(page)
                window.state.scrollTo('demos', page)
              }}
            />
          )}
        </div>
      )}
    </>
  )
}

const useStyles = makeStyles((theme: Theme) => ({
  navBlock: {
    position: 'absolute',
    left: '50%',
    bottom: 0,
    transform: 'translate(-50%, -50%)',
    zIndex: 1301,
    [theme.breakpoints.down('lg')]: {
      bottom: '-20px'
    },
    [theme.breakpoints.down('md')]: {
      bottom: '-25px'
    }
  },
  root: {
    height: 'calc(100% - 105px)',
    width: '35%',
    padding: '0 5%',
    top: '105px',
    display: 'flex',
    alignItems: 'center',
    position: 'absolute',
    backgroundColor: 'transparent',
    zIndex: 1300,
    color: '#000',
    overflowY: 'auto',
    textAlign: 'left',
    [theme.breakpoints.down('lg')]: {
      height: 'calc(100% - 90px)',
      top: '90px'
    },
    [theme.breakpoints.down('md')]: {
      height: 'calc(100% - 60px)',
      top: '60px'
    }
  },
  button: {
    width: 200
  },
  inlineButton: {
    color: '#000',
    margin: '0 .5rem',
    fontFamily: 'Verizon-Regular',
    textDecoration: 'underline'
  },
  forwardArrow: {
    position: 'relative',
    top: '2px',
    left: '4px',
    WebkitTransform: 'rotate(180deg)',
    transform: 'rotate(180deg)'
  },
  fontSize14: {
    fontSize: '14px'
  },
  transition: {
    opacity: 1,
    animationName: '$fadeInOpacity',
    animationIterationCount: 1,
    animationTimingFunction: 'ease-in',
    animationDuration: '0.75s'
  },
  transitionOut: {
    animationName: '$fadeOutOpacity',
    animationIterationCount: 1,
    animationTimingFunction: 'ease-out',
    animationDuration: '0.75s',
    opacity: 0
  },
  '@keyframes fadeInOpacity': {
    '0%': {
      opacity: 0
    },
    '100%': {
      opacity: 1
    }
  },
  '@keyframes fadeOutOpacity': {
    '0%': {
      opacity: 1
    },
    '100%': {
      opacity: 0
    }
  }
}))
